import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useCreateSite, useDeleteSite, useMyProjects, useSites, useUpdateSite } from '../api/hooks'
import type { ProjectStatus, SiteCreateRequest, SiteSummary } from '../api/types'
import { useAuth } from '../auth/useAuth'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { PageHeader } from '../components/PageHeader'
import { ActiveChip, StatusChip } from '../components/StatusChip'

const STATUSES: ProjectStatus[] = [
  'PLANNED',
  'IN_PROGRESS',
  'ON_HOLD',
  'COMPLETED',
  'CLOSED',
  'CANCELLED',
]

export function SitesPage() {
  const { can } = useAuth()
  const projects = useMyProjects()
  const [projectId, setProjectId] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const [dialogOpen, setDialogOpen] = useState(false)

  const [editing, setEditing] = useState<SiteSummary | null>(null)

  const query = useSites({ projectId: projectId || undefined, q: search || undefined, page, size })
  const deleteSite = useDeleteSite()

  return (
    <>
      <PageHeader
        title="Sites"
        subtitle="The unit of daily execution. One daily report per site, per date, per shift."
        actions={
          can('SITE:CREATE') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              disabled={(projects.data?.length ?? 0) === 0}
            >
              New site
            </Button>
          )
        }
      />

      {(projects.data?.length ?? 0) === 0 && !projects.isLoading && (
        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography color="text.secondary">
            Create a project first - every site belongs to one.
          </Typography>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Project"
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="">All projects</MenuItem>
          {(projects.data ?? []).map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.code} - {p.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search name or code"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 240 }}
        />
      </Paper>

      {query.isLoading && <LoadingBlock />}
      {query.error && <ErrorBlock error={query.error} />}
      {deleteSite.error && <ErrorBlock error={deleteSite.error} title="Could not retire the site" />}

      {query.data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Site</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>District</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Timezone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data.content.map((site) => (
                <TableRow key={site.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{site.code}</TableCell>
                  <TableCell>{site.name}</TableCell>
                  <TableCell>{projects.data?.find((p) => p.id === site.projectId)?.name ?? '-'}</TableCell>
                  <TableCell>{site.district ?? '-'}</TableCell>
                  <TableCell>{site.state ?? '-'}</TableCell>
                  <TableCell>{site.timezone}</TableCell>
                  <TableCell>
                    <StatusChip status={site.status} />
                  </TableCell>
                  <TableCell>
                    <ActiveChip active={site.active} />
                  </TableCell>
                  <TableCell align="right">
                    {can('SITE:UPDATE') && (
                      <Tooltip title="Edit site">
                        <IconButton size="small" onClick={() => setEditing(site)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {can('SITE:DELETE') && site.active && (
                      <Tooltip title="Retire site">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (window.confirm(`Retire "${site.name}"?`)) {
                              deleteSite.mutate(site.id)
                            }
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {query.data.content.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No sites match these filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={query.data.page.totalElements}
            page={query.data.page.number}
            rowsPerPage={query.data.page.size}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setSize(Number(e.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      <SiteDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        projects={(projects.data ?? []).map((p) => ({ id: p.id, label: `${p.code} - ${p.name}` }))}
        defaultProjectId={projectId}
      />
      <SiteDialog
        open={Boolean(editing)}
        site={editing ?? undefined}
        onClose={() => setEditing(null)}
        projects={(projects.data ?? []).map((p) => ({ id: p.id, label: `${p.code} - ${p.name}` }))}
        defaultProjectId={projectId}
      />
    </>
  )
}

/** One dialog for both: with a site it edits, without one it creates. */
function SiteDialog({
  open,
  site,
  onClose,
  projects,
  defaultProjectId,
}: {
  open: boolean
  site?: SiteSummary
  onClose: () => void
  projects: { id: string; label: string }[]
  defaultProjectId: string
}) {
  const createSite = useCreateSite()
  const updateSite = useUpdateSite()
  const [form, setForm] = useState<SiteCreateRequest>({
    projectId: defaultProjectId,
    code: '',
    name: '',
    timezone: 'Asia/Kolkata',
    status: 'PLANNED',
  })

  // Loading the row into the form when the dialog opens on a different site.
  const [loadedId, setLoadedId] = useState<string | null>(null)
  if (open && site && loadedId !== site.id) {
    setLoadedId(site.id)
    setForm({
      projectId: site.projectId,
      code: site.code,
      name: site.name,
      district: site.district ?? '',
      state: site.state ?? '',
      timezone: site.timezone,
      status: site.status,
    })
  }

  const saving = createSite.isPending || updateSite.isPending
  const error = site ? updateSite.error : createSite.error

  function update<K extends keyof SiteCreateRequest>(key: K, value: SiteCreateRequest[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSave() {
    const body: SiteCreateRequest = {
      ...form,
      code: form.code.trim(),
      name: form.name.trim(),
      latitude: form.latitude ?? null,
      longitude: form.longitude ?? null,
      geofenceRadiusM: form.geofenceRadiusM ?? null,
      startDate: form.startDate || null,
      plannedCompletionDate: form.plannedCompletionDate || null,
    }
    try {
      if (site) {
        await updateSite.mutateAsync({ id: site.id, body })
        setLoadedId(null)
      } else {
        await createSite.mutateAsync(body)
        setForm({ projectId: defaultProjectId, code: '', name: '', timezone: 'Asia/Kolkata', status: 'PLANNED' })
      }
      onClose()
    } catch {
      // Rendered from the mutation state.
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{site ? `Edit ${site.code}` : 'New site'}</DialogTitle>
      <DialogContent dividers>
        {Boolean(error) && (
          <Box sx={{ mb: 2 }}>
            <ErrorBlock
              error={error}
              title={site ? 'Could not save the site' : 'Could not create the site'}
            />
          </Box>
        )}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, pt: 1 }}>
          <TextField
            select
            label="Project"
            required
            value={form.projectId}
            onChange={(e) => update('projectId', e.target.value)}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Code"
            required
            value={form.code}
            disabled={Boolean(site)}
            helperText={site ? 'Set once and not editable' : ' '}
            onChange={(e) => update('code', e.target.value)}
          />
          <TextField
            label="Name"
            required
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            sx={{ gridColumn: { sm: 'span 2' } }}
          />
          <TextField
            label="Address"
            value={form.address ?? ''}
            onChange={(e) => update('address', e.target.value)}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
          <TextField label="District" value={form.district ?? ''} onChange={(e) => update('district', e.target.value)} />
          <TextField label="State" value={form.state ?? ''} onChange={(e) => update('state', e.target.value)} />
          <TextField label="Pincode" value={form.pincode ?? ''} onChange={(e) => update('pincode', e.target.value)} />
          <TextField
            label="Latitude"
            type="number"
            value={form.latitude ?? ''}
            onChange={(e) => update('latitude', e.target.value === '' ? null : Number(e.target.value))}
            helperText="-90 to 90"
          />
          <TextField
            label="Longitude"
            type="number"
            value={form.longitude ?? ''}
            onChange={(e) => update('longitude', e.target.value === '' ? null : Number(e.target.value))}
            helperText="-180 to 180"
          />
          <TextField
            label="Geofence radius (m)"
            type="number"
            value={form.geofenceRadiusM ?? ''}
            onChange={(e) => update('geofenceRadiusM', e.target.value === '' ? null : Number(e.target.value))}
            helperText="Optional; warns on out-of-radius submission"
          />
          <TextField
            label="Timezone"
            value={form.timezone ?? ''}
            onChange={(e) => update('timezone', e.target.value)}
            helperText="IANA name, e.g. Asia/Kolkata"
          />
          <TextField
            select
            label="Status"
            value={form.status ?? 'PLANNED'}
            onChange={(e) => update('status', e.target.value as ProjectStatus)}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s.replace('_', ' ')}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Start date"
            type="date"
            value={form.startDate ?? ''}
            onChange={(e) => update('startDate', e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.projectId || !form.code.trim() || !form.name.trim()}
        >
          {saving ? 'Saving...' : site ? 'Save changes' : 'Create site'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
